import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Session } from './api/client';
import { activeDestination, clearActive, saveDestination } from './state/destinations';
import { Today } from './screens/Today';
import { FirstRun } from './screens/FirstRun';
import { Capture } from './screens/Capture';
import { EntryActions } from './screens/EntryActions';
import { ReflectionEditor } from './screens/ReflectionEditor';
import { Reflections } from './screens/Reflections';
import { Settings } from './screens/Settings';
import { Week } from './screens/Week';
import { Month } from './screens/Month';
import { Index, type Destination } from './screens/Index';
import { ReadingLog } from './screens/ReadingLog';
import { Gratitude } from './screens/Gratitude';
import { Filters } from './screens/Filters';
import { EventDetail } from './screens/EventDetail';
import { HabitsGrid } from './screens/HabitsGrid';
import { Commonplace } from './screens/Commonplace';
import { RitualBand } from './screens/RitualBand';
import { InstallPrompt } from './screens/InstallPrompt';
import { settle, startSession } from './state/install';
import type { WireCalendarEvent, WireCapabilities } from './api/wire';
import type { WireEntry } from './api/wire';

import { StatusRow } from './components/chrome/StatusRow.jsx';
import { SpreadBar } from './components/composed/SpreadBar';
import { FutureLog } from './screens/FutureLog';
import { previousDay } from './api/adapter';
import { indexSummary, type BusyFilter, type IndexSummary } from './api/summary';
import { CaptureBar } from './components/chrome/CaptureBar.jsx';
import { UndoToast } from './components/composed/UndoToast';
import { Pressable } from './components/a11y/Pressable';
import { Banner } from './components/composed/Banner';
import { TabBarNav } from './components/a11y/TabBarNav';
import { SectionRail } from './components/a11y/SectionRail';
import { useLayout } from './layout/useLayout';
import { RAIL_WIDTH, ritualFor, type Layout } from './layout/breakpoints';
import {
  SCHEDULE_ROW_RESERVED,
  SCHEDULE_TITLE_SIZE,
  droppedBadge,
  fitLayout,
} from './layout/measure';
import { describeError, request } from './api/client';
import type { WireDay } from './api/wire';
import { applyTheme, storedTheme, type ThemeChoice } from './state/theme';
import { readPreferences, writePreferences, type Preferences } from './state/preferences';
import { activeDestination as currentDestination } from './state/destinations';
import { pending, replay } from './state/queue';
import { inverseOf, nextDay, transition } from './api/mutations';
import { subscribe, type Invalidation, type StreamState } from './state/events';

const TABS = ['TODAY', 'WEEK', 'MONTH', 'INDEX'];

/** The layout after R12's measurement, held steady while a day is still loading.
 *
 *  The measurement needs the days' calendar titles, and those arrive after the
 *  first paint — so the honest sequence is: render what the breakpoint earned,
 *  measure, then drop a column if a title would clip. One reflow on first load
 *  is inherent to measuring rather than guessing, and the badge says why.
 *
 *  Two things keep it from oscillating. The titles are cached **by date** and
 *  never evicted, so dropping a column — which unmounts the page that reported
 *  one of them — does not erase the evidence and flip the decision back. And the
 *  candidate is always the layout the *breakpoint* earned, never the one
 *  currently rendered, so the decision is recomputed from a fixed input rather
 *  than from its own output. While any shown day is still unmeasured the last
 *  decision stands, so paging to a new date does not flash through two columns.
 */
function useMeasuredLayout(
  width: number,
  earned: Layout,
  titles: Record<string, string>,
  viewingDate: string | null,
  journalDate: string | null,
): Layout {
  const settled = useRef<Layout>(earned);
  const dates = dayPages(viewingDate, journalDate, earned.days).map((d) => d ?? journalDate);

  const known = dates.filter((d): d is string => Boolean(d) && titles[d as string] !== undefined);
  if (known.length !== dates.length) return settled.current;

  const longest = known.reduce((acc, d) => {
    const title = titles[d] ?? '';
    return title.length > acc.length ? title : acc;
  }, '');
  const fitted = fitLayout(width, earned, longest, SCHEDULE_TITLE_SIZE, SCHEDULE_ROW_RESERVED);
  settled.current = fitted.dropped
    ? { ...fitted.layout, badge: droppedBadge(earned, fitted.layout) }
    : fitted.layout;
  return settled.current;
}

/** Which days a spread shows. The day you are on is always the rightmost page,
 *  with the day before it to its left — the terminal client's reading order,
 *  where yesterday sits beside today rather than after it. `null` means "let the
 *  server decide what today is", which only the anchor page may do. */
function dayPages(viewing: string | null, journalDate: string | null, days: 1 | 2): (string | null)[] {
  if (days === 1) return [viewing];
  const anchor = viewing ?? journalDate;
  if (!anchor) return [null];
  const before = new Date(`${anchor}T00:00:00Z`);
  before.setUTCDate(before.getUTCDate() - 1);
  return [before.toISOString().slice(0, 10), viewing];
}

/** `AUG 2026`, for the capture bar's placeholder on the month face. */
function monthCaption(journalDate: string | null): string {
  if (!journalDate) return 'this month';
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const [y, m] = journalDate.split('-');
  return `${months[Number(m) - 1] ?? m} ${y}`;
}

function currentTime(): string {
  const now = new Date();
  return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
}

export function App() {
  const [session, setSession] = useState<Session | null>(() => {
    const destination = activeDestination();
    return destination
      ? { baseUrl: destination.baseUrl, token: destination.token.value }
      : null;
  });
  const [tab, setTab] = useState('TODAY');
  const [capturing, setCapturing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // The inverse of the last action, if it has one. The action sheet promises
  // `one tap · undoable` on Complete, and until this was wired the toast had no
  // UNDO to offer — the promise was simply untrue.
  const [undo, setUndo] = useState<(() => Promise<void>) | null>(null);
  // Bumped after a capture so Today refetches rather than patching locally —
  // the same "refetch, never reconcile" rule the TUI uses, so the screen can
  // never disagree with the service.
  const [generation, setGeneration] = useState(0);
  const [online, setOnline] = useState(() => navigator.onLine);
  // Which journal day "today" is depends on the server's timezone, so the
  // server is asked rather than guessed from the device clock.
  const [journalDate, setJournalDate] = useState<string | null>(null);
  const [theme, setTheme] = useState<ThemeChoice>(() => storedTheme());
  // Client preferences the service has no opinion about. Persisted on change so
  // a reload keeps them, and threaded down so they actually change behaviour
  // rather than only appearing in Settings.
  const [preferences, setPreferences] = useState<Preferences>(() => readPreferences());
  // Width buys more journal, never denser journal. Orientation matters too, so
  // this cannot be a media query — see `layout/breakpoints.ts`.
  const { width, layout: earned } = useLayout();
  // R12: the fallback is measured, not guessed. The breakpoint decides what the
  // viewport has *earned*; the widest calendar title on the days being shown
  // decides whether that many columns is actually legible, and a column is
  // dropped when it is not. `layout/measure.ts` holds the rule.
  const [titles, setTitles] = useState<Record<string, string>>({});
  const noteTitles = useCallback((journalDate: string, longest: string) => {
    setTitles((current) =>
      current[journalDate] === longest ? current : { ...current, [journalDate]: longest },
    );
  }, []);
  // Saved from `Continue offline · sync later`: the app knows where the journal
  // lives and has no credential for it. Reads cannot work; capture can, because
  // the queue is keyed by destination and never needs a token to enqueue.
  const unauthenticated = Boolean(session) && !session!.token.trim();
  const captureField = useRef<HTMLInputElement | null>(null);
  const bottomBar = useRef<HTMLDivElement | null>(null);
  // The capture bar and tab bar are fixed, so the scrolling region has to
  // reserve their height. A constant was 15px short at every width, which hid
  // the last rapid-log row's bottom edge permanently — and a constant cannot be
  // right anyway, since `env(safe-area-inset-bottom)` differs per device and
  // changes on rotation.
  const [barHeight, setBarHeight] = useState(120);
  const [acting, setActing] = useState<WireEntry | null>(null);
  const [editingReflection, setEditingReflection] = useState(false);
  const [inSettings, setInSettings] = useState(false);
  // A day other than the server's today, reached from the monthly calendar or
  // by paging. Null means "whatever the server calls today".
  const [viewingDate, setViewingDate] = useState<string | null>(null);
  /** The reflections review opens the editor at a date that is not the day on
   *  screen, so it cannot go through viewingDate — that would also page the
   *  daily log underneath and leave the reader somewhere else on close. */
  const [reflectionAt, setReflectionAt] = useState<string | null>(null);
  const measured = useMeasuredLayout(width, earned, titles, viewingDate, journalDate);
  // `focus` pins one day however wide the screen — the terminal client's toggle.
  // `◆ FOCUSED` is the design's own word for the one-day mode, so the badge says
  // that rather than inventing a second name for it.
  const layout: Layout =
    preferences.dayView === 'focus' && measured.days === 2
      ? { ...measured, days: 1, ritual: ritualFor(1, measured.lanes), badge: 'FOCUSED' }
      : measured;
  // The spread's own identity: which two days it holds. Null unless there are
  // two pages, and null until the server has said what today is, since without
  // that there is no anchor to range from.
  /** The one route to a destination, shared by the Index and the rail. */
  const openDestination = (where: Destination, filter?: BusyFilter) => {
    if (where === 'settings') return setInSettings(true);
    // The three logs are tabs, not collections: jumping to them is the same
    // navigation the tab bar does.
    if (where === 'today' || where === 'week' || where === 'month') {
      setCollection(null);
      setOpenMonth(null);
      return setTab(where.toUpperCase());
    }
    setOpenFilter(where === 'filters' ? (filter ?? null) : null);
    setCollection(where);
    // A collection lives under the Index, so the rail's selection has to move
    // there too or the page and the navigation disagree.
    setTab('INDEX');
  };

  const anchorDate = viewingDate ?? journalDate;
  // The digits the rail prints beside `GO TO`. Bound for every tab there is, so
  // the printed number and the key that works cannot drift apart.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0 || index >= TABS.length) return;
      event.preventDefault();
      setCollection(null);
      setOpenFilter(null);
      setTab(TABS[index] as string);
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  // `h` and `l` page the day, the same keys the terminal client uses and the
  // ones the spread bar prints as hints. An iPad with a keyboard attached is the
  // case the wide frame was drawn for, so the hint has to be a real binding.
  useEffect(() => {
    if (tab !== 'TODAY' || !anchorDate) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Never while typing: `h` and `l` are letters before they are commands.
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (event.key === 'h') setViewingDate(previousDay(anchorDate));
      else if (event.key === 'l') setViewingDate(nextDay(anchorDate));
      else return;
      event.preventDefault();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [tab, anchorDate]);

  // Only while the Daily Log is what is on screen. This is the *day's* chrome —
  // a date range, keys that page a day, and a two-day/one-day switch — and it
  // was rendering on every tab, so a book index sat under `DAILY LOG · TWO-DAY
  // SPREAD` beside arrows that moved a day you could not see. The `h`/`l`
  // bindings were already scoped to TODAY, which made it worse: the arrows
  // worked there and the keys did not.
  const spread =
    tab === 'TODAY' && earned.days === 2 && anchorDate
      ? { from: layout.days === 2 ? previousDay(anchorDate) : anchorDate, to: anchorDate }
      : null;
  const [capabilities, setCapabilities] = useState<WireCapabilities | null>(null);
  const [collection, setCollection] = useState<Destination | null>(null);
  // The journal's own facts, for the rail and the Index. Deliberately not
  // fetched at boot on a phone: it is nine requests for a screen that may never
  // be opened, and this app is used in the thirty seconds before a thought is
  // lost.
  const [summary, setSummary] = useState<IndexSummary | null>(null);
  // Where a filter row in the rail was pointing, so the Filters screen opens on
  // that filter's tasks rather than on its index. A row labelled `/Product 12`
  // that lands you on a list of every stream is a half-kept promise.
  const [openFilter, setOpenFilter] = useState<BusyFilter | null>(null);
  const wantsSummary = layout.nav === 'rail' || tab === 'INDEX';
  useEffect(() => {
    if (!session || !journalDate || !wantsSummary) return;
    let cancelled = false;
    void (async () => {
      const next = await indexSummary(session, journalDate, capabilities);
      if (!cancelled) setSummary(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, journalDate, capabilities, wantsSummary, generation]);
  const [queued, setQueued] = useState(0);
  // Set while a filtered task list is open, so the capture bar names where the
  // capture will land and the sheet starts with that token in the field.
  const [captureInto, setCaptureInto] = useState<string | null>(null);
  // Which month the Month face should open on, when something sent it there.
  const [openMonth, setOpenMonth] = useState<string | null>(null);
  // Reported by the Month screen, which owns its own face and month.
  const [monthFace, setMonthFace] = useState<{ face: 'CALENDAR' | 'INBOX'; month: string } | null>(
    null,
  );
  // Stable, and it drops an identical report. An inline arrow here made the
  // Month screen's effect re-run on every render, which set state, which
  // rendered again — a loop that showed up as the page never settling.
  const noteMonthFace = useCallback((face: 'CALENDAR' | 'INBOX', month: string) => {
    setMonthFace((current) =>
      current?.face === face && current.month === month ? current : { face, month },
    );
  }, []);

  /** Where a capture would land, or null where the page cannot hold one.
   *
   *  Taken from the terminal client, which is stricter than this app had been and
   *  right to be: a page only accepts what it can show you. There, `.` is refused
   *  outright on the future log and the month calendar, the weekly page needs a
   *  focused day column before it will take anything, and the month log accepts a
   *  capture only on its tasks face — into the month, not the day.
   *
   *  This app had a capture bar on every screen aimed at *today*, so filing from
   *  a week you were only reading, or a month you had paged to, silently landed
   *  somewhere else. The bar is now absent where there is nothing to aim at.
   */
  const captureTarget: { kind: 'day'; date: string | null } | { kind: 'month'; month: string } | null =
    collection === 'future'
      ? null
      : tab === 'WEEK'
        // The weekly page has no focused day on touch, and the terminal client
        // refuses a capture without one rather than guessing at today.
        ? null
        : tab === 'MONTH'
          ? monthFace?.face === 'INBOX'
            ? { kind: 'month', month: monthFace.month }
            : null
          // Deliberately not gated on knowing the date: with no token there is no
          // journal date to know, and capture is the one thing
          // `Continue offline · sync later` promises still works. The server
          // resolves the day at replay.
          : { kind: 'day', date: anchorDate };

  // Once, on the third session. `startSession` also does the counting, so it
  // must be called exactly once per app start — hence the lazy initialiser
  // rather than an effect, which would run twice under StrictMode.
  const [installDue, setInstallDue] = useState(() => startSession());
  const [installEvent, setInstallEvent] = useState<{ prompt: () => Promise<void> } | null>(null);
  // A calendar event being read. Read-only everywhere, per R9.
  const [viewingEvent, setViewingEvent] = useState<{
    event: WireCalendarEvent;
    asOf: string | null;
    freshness: string | null;
  } | null>(null);
  // The latest change recorded by the service, handed to whichever screen is
  // showing. Screens own the decision to reload, because only they know the
  // range they are displaying.
  const [invalidation, setInvalidation] = useState<Invalidation | null>(null);
  // Where the event stream starts. Set once from a day projection's
  // `event_cursor`, which is the journal's global high-water mark — subscribing
  // without it replays the entire change history. Cleared only when the server
  // rejects it, which forces a refetch to learn a fresh one.
  const [subscribeFrom, setSubscribeFrom] = useState<number | null>(null);
  const [stream, setStream] = useState<StreamState>('connecting');

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    writePreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    // Chromium hands over a deferred prompt; Safari never does, and the sheet
    // explains the manual route there instead of offering a dead button.
    const capture = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as unknown as { prompt: () => Promise<void> });
    };
    addEventListener('beforeinstallprompt', capture);
    return () => removeEventListener('beforeinstallprompt', capture);
  }, []);

  // A collection that the server has switched off must not stay reachable just
  // because it was open when the toggle flipped. R7: no dead routes.
  useEffect(() => {
    if (!collection || !capabilities?.collections) return;
    const gate: Partial<Record<string, boolean>> = {
      reading: capabilities.collections.reading_log,
      gratitude: capabilities.collections.gratitude_log,
      habits: capabilities.collections.habits,
      commonplace: capabilities.collections.commonplace_books,
    };
    if (gate[collection] === false) setCollection(null);
  }, [collection, capabilities]);

  // Anything that leaves the filters collection clears the capture target,
  // whichever route it took — the tab bar, the Index root, or a collection
  // switch. Filters reports its own selection while it is mounted; this covers
  // it being unmounted.
  useEffect(() => {
    if (collection !== 'filters') setCaptureInto(null);
  }, [collection]);

  useEffect(() => {
    const element = bottomBar.current;
    if (!element) return;
    const measure = () => setBarHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [session]);
  const [clock, setClock] = useState(() => currentTime());

  useEffect(() => {
    const tick = setInterval(() => setClock(currentTime()), 30_000);
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    addEventListener('online', up);
    addEventListener('offline', down);
    return () => {
      clearInterval(tick);
      removeEventListener('online', up);
      removeEventListener('offline', down);
    };
  }, []);

  const refreshQueue = async () => {
    const destination = currentDestination();
    if (!destination) return;
    setQueued((await pending(destination)).length);
  };

  useEffect(() => {
    void refreshQueue();
  }, [generation]);

  useEffect(() => {
    if (!session || !online) return;
    let cancelled = false;
    (async () => {
      const destination = currentDestination();
      if (!destination) return;
      const report = await replay(session, destination);
      if (cancelled) return;
      await refreshQueue();
      if (report.sent > 0) {
        setToast(`Sent ${report.sent} queued ${report.sent === 1 ? 'capture' : 'captures'}`);
        setTimeout(() => setToast(null), 4000);
        setGeneration((n) => n + 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, online]);

  useEffect(() => {
    if (!session || unauthenticated) return;
    let cancelled = false;
    request<WireCapabilities>(session, '/v1/capabilities')
      .then(({ body }) => {
        if (!cancelled) setCapabilities(body);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session, generation]);

  useEffect(() => {
    if (!session || unauthenticated) return;
    let cancelled = false;
    request<WireDay>(session, '/v1/days/today')
      .then(({ body }) => {
        if (cancelled) return;
        setJournalDate(body.journal_date);
        setSubscribeFrom((current) => current ?? body.event_cursor);
      })
      .catch(() => {
        // Today's own load surfaces the failure; capture simply falls back to
        // letting the server place the entry.
      });
    return () => {
      cancelled = true;
    };
  }, [session, generation]);

  useEffect(() => {
    if (!session || unauthenticated || subscribeFrom === null) return;
    return subscribe({
      session,
      from: subscribeFrom,
      onInvalidate: setInvalidation,
      onState: setStream,
      onCursorLost: () => {
        // The cursor is ahead of the log — a restored or rotated database.
        // Refetching yields a fresh high-water mark, which re-arms this effect.
        setSubscribeFrom(null);
        setGeneration((n) => n + 1);
      },
    });
  }, [session, subscribeFrom]);

  // The documented degraded state: with no stream, fall back to refetching when
  // the app is next looked at. This is the TUI's consistency model, not a
  // failure — but it has to actually happen, or a dead stream reads as a live
  // one showing stale data.
  useEffect(() => {
    if (stream !== 'degraded') return;
    const refetch = () => {
      if (document.visibilityState === 'visible') setGeneration((n) => n + 1);
    };
    document.addEventListener('visibilitychange', refetch);
    return () => document.removeEventListener('visibilitychange', refetch);
  }, [stream]);

  /** A swipe's transition, sharing the sheet's toast and undo.
   *
   *  Deliberately the same code path as `EntryActions`: a gesture is a shortcut
   *  to a mutation, not a second implementation of it, so `undo from the toast`
   *  means the same thing whichever way the action was started. */
  async function quickAction(entry: WireEntry, action: 'complete' | 'migrate') {
    if (!session) return;
    const label = action === 'complete' ? 'Complete' : 'Migrate to tomorrow';
    try {
      const result =
        action === 'complete'
          ? await transition(session, entry.id, 'complete')
          : await transition(session, entry.id, 'migrate', {
              destination_date: nextDay(entry.active_date ?? journalDate ?? ''),
            });
      if (result.kind === 'conflict') {
        // A raced swipe opens the sheet, which is where both revisions are shown.
        setActing(entry);
        return;
      }
      setGeneration((n) => n + 1);
      setToast(`${label} · ${entry.content}`);
      const back = inverseOf(action === 'complete' ? 'complete' : 'migrate');
      setUndo(back ? () => async () => void (await transition(session, entry.id, back)) : null);
      setTimeout(() => {
        setToast(null);
        setUndo(null);
      }, 4000);
    } catch (caught) {
      setToast(`! ${describeError(caught)}`);
      setTimeout(() => setToast(null), 4000);
    }
  }

  if (!session) return <FirstRun onConnected={setSession} />;

  return (
    // A fixed-height shell: the content scrolls inside it and the capture bar
    // sits below it in the flow. The bar used to be `position: fixed` over a
    // scrolling document, and on the iPhone Duo it rode up the screen with the
    // content — in flow, there is nothing for iOS to get wrong.
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The status row spans the page, so it also has to start past the rail —
          otherwise the rail's first label sits on top of the clock. */}
      <div style={{ paddingLeft: layout.nav === 'rail' ? RAIL_WIDTH : 0 }}>
        {spread ? (
          <SpreadBar
            label={layout.badge ?? 'TWO-DAY SPREAD'}
            // Only where the choice is real: if the measurement already forced
            // one day, there is nothing to switch to.
            onToggleDays={
              measured.days === 2 || preferences.dayView === 'focus'
                ? () =>
              setPreferences((current) => {
                const next: Preferences = {
                  ...current,
                  dayView: current.dayView === 'focus' ? 'auto' : 'focus',
                };
                writePreferences(next);
                return next;
              })
                : null
            }
            from={spread.from}
            to={spread.to}
            queued={queued}
            offline={!online}
            onPrevious={() => setViewingDate(previousDay(spread.to))}
            onNext={() => setViewingDate(nextDay(spread.to))}
          />
        ) : (
          <StatusRow time={clock} queued={queued} battery="" offline={!online} />
        )}
        {/* A persistent condition, so it sits with the status row rather than in
            the scrolling content — where it ended up underneath the fixed tab
            bar and its own action could not be tapped. */}
        {unauthenticated ? (
          <div style={{ padding: '0 var(--bujo-gutter) var(--bujo-space-2)' }}>
            <Banner
              kind="offline"
              text="NO TOKEN · captures are queued on this device"
              action="ADD ONE"
              onAction={() => setInSettings(true)}
            />
          </div>
        ) : null}
      </div>

      <div
        style={{
          flex: 1,
          // Without this a flex child will not shrink below its content, and
          // nothing would ever scroll.
          minHeight: 0,
          overflowY: 'auto',
          // The rail is fixed, so the page has to inset past it.
          paddingLeft: layout.nav === 'rail' ? RAIL_WIDTH : 0,
        }}
      >
        {tab === 'TODAY' ? (
          <>
            {/* One page or two, decided by width and orientation. Two is the
                ceiling at every width — past the two-lane spread the extra room
                becomes breathing space, never a third day. */}
            {/* Two pages fill the scroller's height so each scrolls on its own
                side of the fold, the way the pages of a book do. */}
            <div
              style={
                layout.days === 2
                  ? { display: 'flex', alignItems: 'stretch', height: '100%' }
                  : undefined
              }
            >
              {dayPages(viewingDate, journalDate, layout.days).map((pageDate, index) => (
                <Today
                  key={`t${pageDate ?? 'today'}:${index}`}
                  session={session}
                  date={pageDate ?? undefined}
                  onOpenActions={setActing}
                  // Each page's reflection is its own day's. The left page has
                  // an explicit date; the right one may be "today" (null), which
                  // the editor already falls back to.
                  onEditReflection={() => {
                    setReflectionAt(pageDate);
                    setEditingReflection(true);
                  }}
                  onChangeDate={setViewingDate}
                  invalidation={invalidation}
                  showDayOfYear={preferences.showDayOfYear}
                  onOpenEvent={(event, range) =>
                    setViewingEvent({ event, asOf: range.asOf, freshness: range.freshness })
                  }
                  lanes={layout.lanes}
                  paired={layout.days === 2}
                  // The left page runs to the middle of the *screen*, not of the
                  // content box. iOS reserves a strip on one side for the clock
                  // on the iPhone Duo, so equal pages put the divider left of the
                  // seam. This row starts at the left safe-area inset, so
                  // `50vw` minus that inset lands the divider on the fold; the
                  // right page takes what is left. Not beside the rail: there is
                  // no seam on a tablet, and centring on the screen would only
                  // squeeze the left page by the rail's width.
                  basis={
                    layout.days === 2 && layout.nav !== 'rail' && index === 0
                      ? 'calc(50vw - env(safe-area-inset-left))'
                      : undefined
                  }
                  includeRitual={layout.ritual === 'in-column'}
                  onMeasureTitles={noteTitles}
                  refresh={generation}
                  // The left page is the day before; the right one is the day
                  // you are on, and it carries the amber edge.
                  showPaging={index === layout.days - 1}
                  active={index === layout.days - 1}
                  // On a spread the breadcrumb, the badge and the pager live in
                  // the row above both pages, so the page draws none of them.
                  chrome={spread ? 'spread' : 'page'}
                  marksToday={pageDate === null || pageDate === journalDate}
                  // One badge per page, and only when the page owns its chrome.
                  badge={!spread && index === layout.days - 1 ? layout.badge : null}
                  onQuickAction={(entry, action) => void quickAction(entry, action)}
                />
              ))}
            </div>
            {layout.ritual === 'band' && (viewingDate ?? journalDate) ? (
              <RitualBand
                key={`rb${viewingDate ?? journalDate}`}
                session={session}
                journalDate={(viewingDate ?? journalDate) as string}
                refresh={generation}
                onEditReflection={() => setEditingReflection(true)}
              />
            ) : null}
          </>
        ) : tab === 'WEEK' && journalDate ? (
          <Week
            key="w"
            session={session}
            journalDate={journalDate}
            onOpenActions={setActing}
            invalidation={invalidation}
            refresh={generation}
            weekStart={preferences.weekStart}
          />
        ) : tab === 'MONTH' && journalDate ? (
          <Month
            key={`m${openMonth ?? 'current'}`}
            session={session}
            journalDate={journalDate}
            startMonth={openMonth ?? undefined}
            refresh={generation}
            onFaceChange={noteMonthFace}
            onOpenDay={(date) => {
              setViewingDate(date === journalDate ? null : date);
              setTab('TODAY');
            }}
            onOpenActions={setActing}
            invalidation={invalidation}
          />
        ) : tab === 'INDEX' && journalDate ? (
          collection === 'future' && journalDate ? (
            <FutureLog
              session={session}
              journalDate={journalDate}
              weekStart={preferences.weekStart}
              onOpenMonth={(month) => {
                // The frame says the month page carries the full text, so a far
                // month opens there rather than expanding in place.
                setCollection(null);
                setOpenMonth(month);
                setTab('MONTH');
              }}
              onBack={() => setCollection(null)}
            />
          ) : collection === 'commonplace' ? (
            <Commonplace session={session} onBack={() => setCollection(null)} />
          ) : collection === 'habits' ? (
            <HabitsGrid
              session={session}
              journalDate={journalDate}
              onBack={() => setCollection(null)}
            />
          ) : collection === 'reading' ? (
            <ReadingLog session={session} journalDate={journalDate ?? ''} />
          ) : collection === 'gratitude' && journalDate ? (
            <Gratitude session={session} journalDate={journalDate} />
          ) : collection === 'reflections' ? (
            <Reflections
              session={session}
              onOpen={(date) => {
                setReflectionAt(date);
                setEditingReflection(true);
              }}
              onBack={() => setCollection(null)}
            />
          ) : collection === 'filters' ? (
            <Filters
              // Remounted when the rail points at a different filter, so the
              // screen opens on it rather than keeping whatever was chosen last.
              key={openFilter ? `${openFilter.kind}:${openFilter.name}` : 'index'}
              session={session}
              initial={openFilter ? { kind: openFilter.kind, name: openFilter.name } : null}
              onOpenActions={setActing}
              onCaptureInto={setCaptureInto}
            />
          ) : (
            <Index
              journalDate={journalDate ?? ''}
              capabilities={capabilities}
              summary={summary}
              onOpen={openDestination}
            />
          )
        ) : (
          <Placeholder tab={tab} />
        )}
      </div>

      {toast ? (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: barHeight + 8, padding: '0 var(--bujo-gutter)', zIndex: 20 }}>
          <UndoToast
            text={toast}
            onUndo={
              undo
                ? () => {
                    const back = undo;
                    setToast(null);
                    setUndo(null);
                    void back().then(() => setGeneration((n) => n + 1));
                  }
                : undefined
            }
          />
        </div>
      ) : null}

      <div
        ref={bottomBar}
        style={{
          flex: 'none',
          // Full width at the two-lane breakpoint, but starting past the rail.
          marginLeft: layout.nav === 'rail' ? RAIL_WIDTH : 0,
          // The tab bar pads itself past the home indicator; with the rail
          // there is no tab bar, so the capture bar has to.
          paddingBottom: layout.nav === 'rail' ? 'env(safe-area-inset-bottom)' : 0,
          background: 'var(--bujo-surface-page)',
          borderTop: '1px solid var(--bujo-rule-heavy)',
          zIndex: 10,
        }}
      >
        {captureTarget ? (
        <Pressable
          label="Capture a task, note, or question"
          onPress={() => {
            setCapturing(true);
            // Synchronous, inside the gesture: iOS Safari will not raise the
            // keyboard from a focus() that happens in a later task.
            captureField.current?.focus();
          }}
        >
          {/* The placeholder names where the capture will land. On the month
              face that is the month, not the day. */}
          <CaptureBar
            placeholder={
              captureInto
                ? `capture into ${captureInto}`
                : captureTarget?.kind === 'month'
                  ? `capture into ${monthCaption(`${captureTarget.month}-01`)}`
                  : 'capture a task, note, question'
            }
          />
        </Pressable>
        ) : null}
        {layout.nav === 'rail' ? null : (
        <TabBarNav
          tabs={TABS}
          active={tab}
          onSelect={(next) => {
            // Tapping INDEX while inside a collection returns to its root, the
            // way a back button would.
            if (next === 'INDEX' && tab === 'INDEX') setCollection(null);
            else if (next !== 'INDEX') setCollection(null);
            // Choosing MONTH means the current month. Without this it stayed on
            // whatever month the Future Log last sent it to.
            setOpenMonth(null);
            setTab(next);
          }}
        />
        )}
      </div>

      {layout.nav === 'rail' ? (
        <SectionRail
          tabs={TABS}
          active={tab}
          collection={collection}
          summary={summary}
          collections={capabilities?.collections ?? {}}
          onOpen={openDestination}
          onSelect={(next) => {
            if (next === 'INDEX' && tab === 'INDEX') setCollection(null);
            else if (next !== 'INDEX') setCollection(null);
            // Choosing MONTH means the current month. Without this it stayed on
            // whatever month the Future Log last sent it to.
            setOpenMonth(null);
            setTab(next);
          }}
        />
      ) : null}

      {acting ? (
        <EntryActions
          session={session}
          entry={acting}
          journalDate={journalDate}
          onDismiss={() => setActing(null)}
          onDone={(summary, back) => {
            setActing(null);
            setGeneration((n) => n + 1);
            setToast(summary);
            setUndo(back ? () => back : null);
            setTimeout(() => {
              setToast(null);
              setUndo(null);
            }, 4000);
          }}
        />
      ) : null}

      {editingReflection && (reflectionAt ?? viewingDate ?? journalDate) ? (
        <ReflectionEditor
          // Remounted per date, so opening a second reflection from the review
          // refetches instead of showing the previous one's prose.
          key={(reflectionAt ?? viewingDate ?? journalDate) as string}
          session={session}
          journalDate={(reflectionAt ?? viewingDate ?? journalDate) as string}
          onDismiss={() => {
            setEditingReflection(false);
            setReflectionAt(null);
          }}
          // A photograph is stored the moment it is chosen, so the day behind
          // the sheet is already stale. Refresh it without the commit toast:
          // nothing was committed, and saying so would be a lie about prose.
          onChanged={() => setGeneration((n) => n + 1)}
          onSaved={() => {
            setEditingReflection(false);
            setReflectionAt(null);
            setGeneration((n) => n + 1);
            setToast('Reflection committed');
            setTimeout(() => setToast(null), 4000);
          }}
        />
      ) : null}

      {/* Above the capture bar, never over it — the product's primary action does
          not get covered by a request. */}
      {installDue && session ? (
        <InstallPrompt
          bottom={barHeight}
          onInstall={
            installEvent
              ? () => {
                  void installEvent.prompt();
                  settle();
                  setInstallDue(false);
                }
              : undefined
          }
          onDismiss={() => {
            settle();
            setInstallDue(false);
          }}
        />
      ) : null}

      {viewingEvent ? (
        <EventDetail
          event={viewingEvent.event}
          asOf={viewingEvent.asOf}
          freshness={viewingEvent.freshness}
          onDismiss={() => setViewingEvent(null)}
        />
      ) : null}

      {inSettings ? (
        <Settings
          session={session}
          theme={theme}
          onTheme={setTheme}
          preferences={preferences}
          onPreferences={setPreferences}
          onToken={(value) => {
            // Same destination, now with a credential — so the queue keeps its
            // namespace and drains rather than being orphaned under a new id.
            saveDestination({
              label: session.baseUrl || 'same-origin',
              baseUrl: session.baseUrl,
              token: value,
            });
            setSession({ baseUrl: session.baseUrl, token: value });
            setInSettings(false);
            setGeneration((n) => n + 1);
          }}
          onDisconnect={() => {
            clearActive();
            setInSettings(false);
            setSession(null);
          }}
          onDismiss={() => {
            setInSettings(false);
            setGeneration((n) => n + 1);
          }}
        />
      ) : null}

      <Capture
        session={session}
        // The page's own target, not the journal's today: paging Today to the
        // 20th and capturing used to file on today instead.
        journalDate={
          captureTarget?.kind === 'month'
            ? `${captureTarget.month}-01`
            : (captureTarget?.date ?? journalDate)
        }
        into={captureTarget?.kind === 'month' ? 'month' : 'day'}
        open={capturing}
        fieldRef={captureField}
        prefill={captureInto}
        onCancel={() => setCapturing(false)}
        onCaptured={(summary) => {
          setCapturing(false);
          setGeneration((n) => n + 1);
          setToast(`Captured · ${summary}`);
          setTimeout(() => setToast(null), 4000);
        }}
        onQueued={(summary) => {
          setCapturing(false);
          void refreshQueue();
          setToast(`\u2301 Queued · ${summary}`);
          setTimeout(() => setToast(null), 4000);
        }}
      />
    </div>
  );
}

/** WEEK and MONTH are U8 and U9. Named rather than hidden, so the
 *  tab bar tells the truth about what exists. */
function Placeholder({ tab }: { tab: string }) {
  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 720, margin: '0 auto' }}>
      <span style={{ font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)', letterSpacing: 'var(--bujo-ls-label)', color: 'var(--bujo-label)' }}>
        {tab}
      </span>
      <p style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-prose) var(--bujo-font-sans)', color: 'var(--bujo-text-meta)' }}>
        Not built yet.
      </p>
    </main>
  );
}
